/**
 * CSV ingest for local (PWA) mode — the only way data enters a test drive.
 *
 * ── Why this exists, and the spec gap it fills ────────────────────────────────────────────
 *
 * [F15's ui-spec](../../../../docs/features/15-local-first-pwa-mode/ui-spec.md) specifies eight
 * screens (IA 35–42) and **every data-entry path among them assumes F01's OAuth connect flow** —
 * screen 38 is explicitly "within the connect flow". But a visitor test-driving the product has
 * no account, no OAuth app, and no reason to grant a new vendor access to their CRM on the first
 * click. CSV is the only ingest that asks nothing of them, and F01 already scopes "CSV/XLSX
 * upload as a pseudo-source" — so the capability is specified, just not its local screen.
 * Recorded as [B-127](../../../../docs/platform/TODO.md) rather than added silently.
 *
 * ── Hand-written rather than a dependency ─────────────────────────────────────────────────
 *
 * RFC 4180 is small, and this runs in a browser where every kB of bundle is paid for by a
 * visitor who has not yet decided they want the product. The rules that actually matter and that
 * naive `split(',')` gets wrong are all handled below: quoted fields containing the delimiter,
 * quoted fields containing newlines, escaped quotes (`""`), CRLF, and a UTF-8 BOM.
 *
 * ⚠️ **Nothing here leaves the browser.** No network call, no upload. That is the entire trust
 * proposition of local mode, and it is a property of this module having no way to make a request
 * rather than a promise made elsewhere.
 */
import type { MatchRecord } from '@dedupr/match-core';
import type { CanonicalField, MappingProposal } from '@dedupr/connectors';

/** A parsed sheet: the header row plus the data rows, all cells as strings. */
export interface ParsedCsv {
  headers: string[];
  rows: string[][];
  /**
   * Rows whose cell count did not match the header.
   *
   * ⚠️ Reported, never silently padded or dropped. A short row usually means an unquoted
   * delimiter inside a value, which shifts every later column — so the *data* is wrong rather
   * than merely missing, and a visitor needs to know before they trust a duplicate count
   * derived from it.
   */
  raggedRows: number;
  delimiter: string;
}

/**
 * Detect the delimiter from the first line.
 *
 * Comma, semicolon and tab, in that order of preference. Semicolon matters more than it looks:
 * Excel on a European locale exports semicolon-delimited CSV by default, so a French or German
 * visitor's very first file would otherwise parse as one enormous single column and report zero
 * duplicates — a silent wrong answer, which is the worst kind.
 */
export function detectDelimiter(sample: string): string {
  const firstLine = sample.split(/\r?\n/, 1)[0] ?? '';
  const candidates = [',', ';', '\t'];
  let best = ',';
  let bestCount = 0;
  for (const d of candidates) {
    // Counted outside quotes, so a comma inside "Smith, Jane" does not win the vote.
    let count = 0;
    let inQuotes = false;
    for (let i = 0; i < firstLine.length; i++) {
      const ch = firstLine[i];
      if (ch === '"') inQuotes = !inQuotes;
      else if (ch === d && !inQuotes) count++;
    }
    if (count > bestCount) {
      bestCount = count;
      best = d;
    }
  }
  return best;
}

/** Parse CSV text. Never throws on malformed input — reports it instead. */
export function parseCsv(text: string, delimiter?: string): ParsedCsv {
  // A UTF-8 BOM would otherwise become part of the first header's name, so `﻿email` would
  // not match the `email` alias and the column would go unmapped — invisibly.
  const clean = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const d = delimiter ?? detectDelimiter(clean);

  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < clean.length; i++) {
    const ch = clean[i]!;

    if (inQuotes) {
      if (ch === '"') {
        // `""` inside a quoted field is one literal quote — the RFC's own escape.
        if (clean[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += ch;
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
    } else if (ch === d) {
      row.push(field);
      field = '';
    } else if (ch === '\n') {
      row.push(field);
      field = '';
      rows.push(row);
      row = [];
    } else if (ch === '\r') {
      // CRLF: the \n case handles the row break, so the \r is simply skipped.
    } else {
      field += ch;
    }
  }
  // The final row, if the file does not end with a newline.
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  // Drop wholly-empty rows: a trailing blank line is normal and is not a data row.
  const nonEmpty = rows.filter((r) => r.some((c) => c.trim().length > 0));
  const headers = (nonEmpty.shift() ?? []).map((h) => h.trim());
  const ragged = nonEmpty.filter((r) => r.length !== headers.length).length;

  return { headers, rows: nonEmpty, raggedRows: ragged, delimiter: d };
}

/* ─────────────────── column mapping — delegated to the provider registry ──────────────────── */

/**
 * Mapping is **not implemented here.** It lives in `@dedupr/connectors`' `proposeMapping`, and this
 * module only applies the result.
 *
 * ⚠️ It *was* implemented here — an alias table, a `canonical()` header normaliser and an
 * `inferMapping` — none of which knew that `AccountId` is Salesforce's company reference or that
 * `Record ID` is HubSpot's. Two mappers over the same problem is the drift the registry exists to
 * prevent: a field name learned in one would never reach the other, and the relay's pull path and
 * the upload path would disagree about the same export from the same system.
 *
 * What this module keeps is the part that genuinely belongs to a file: RFC 4180 parsing, delimiter
 * detection, and turning a confirmed mapping into engine records.
 */

/**
 * The id `toRecords` gives row `i` — exported so the store-backed export derives the SAME id when it
 * rebuilds the row lookup from `source_raw`. Two independent derivations of this id is how a merged
 * row silently loses its original columns.
 */
export function rowIdFor(row: string[], i: number, idColumn: number | null): string {
  /* Row number when the sheet has no id column — 1-based and counting the header, so it matches
     what the visitor sees in their spreadsheet. `row-` prefixed so an id is never mistaken for a
     real CRM id. */
  return cell(row, idColumn) ?? `row-${i + 2}`;
}

/** Read one cell, treating blank as absent — a blank is not a value the engine may block on. */
function cell(row: string[], index: number | null | undefined): string | null {
  if (index === null || index === undefined) return null;
  const v = row[index];
  return v === undefined || v.trim() === '' ? null : v.trim();
}

/**
 * Apply a confirmed mapping, producing engine records.
 *
 * Takes the registry's `MappingProposal` — after any human override — rather than a bespoke shape,
 * so the confirmation screen's output is exactly what runs.
 */
export function toRecords(parsed: ParsedCsv, proposal: MappingProposal): MatchRecord[] {
  const columnOf = (field: CanonicalField): number | null =>
    proposal.fields.find((f) => f.field === field)?.column ?? null;

  const idCol = columnOf('external_id');
  const fullCol = columnOf('full_name');
  const firstCol = columnOf('first_name');
  const lastCol = columnOf('last_name');
  const emailCol = columnOf('email');
  const companyCol = columnOf('company');
  const phoneCol = columnOf('phone');
  const domainCol = columnOf('domain');
  /* V-19b: the object the mapping resolved against says what these records ARE. A companies
     sheet produces company-kind records, which the engine partitions into its own run. */
  const kind = proposal.fingerprint.object.kind;

  return parsed.rows.map((row, i) => {
    /* Join first + last when there is no single full-name column. The engine matches on one name
       field, so the join has to happen somewhere; doing it here keeps the *mapping* honest about
       what the file actually contains — the registry reports the two columns it found rather than a
       synthesised one the visitor cannot point at in their spreadsheet. */
    const joined = [cell(row, firstCol), cell(row, lastCol)].filter(Boolean).join(' ') || null;

    return {
      // Unique per row because `runMatch` refuses duplicate ids outright — see `rowIdFor`.
      id: rowIdFor(row, i, idCol),
      full_name: cell(row, fullCol) ?? joined,
      email: cell(row, emailCol),
      company: cell(row, companyCol),
      phone: cell(row, phoneCol),
      domain: cell(row, domainCol),
      kind,
    };
  });
}
